import { validaCPF } from '@/shared/utils/cpfs';
import { EstadosCivisList } from '@/modules/people/enums/estado-civil.enum';
import { NavigationView } from '@/shared/constants/navigation-view.enum';
import type { ReadCandidatoDto, ReadPersonDto } from '@/api.types';

const SITUACAO_LABELS: Record<string, string> = {
  rascunho: 'Rascunho',
  inscricao_realizada: 'Inscrição Realizada',
  inscricao_validada: 'Inscrição Validada',
  indeferida: 'Indeferida',
  classificada: 'Classificada',
  convocada: 'Convocada',
  matriculado: 'Matriculado(a)',
};

/** Situações que o admin pode definir manualmente (exceto matriculado, definido pela promoção). */
export const SITUACOES_EDITAVEIS_ADMIN = [
  'inscricao_realizada',
  'inscricao_validada',
  'indeferida',
  'classificada',
  'convocada',
] as const;

export type SituacaoEditavelAdmin = (typeof SITUACOES_EDITAVEIS_ADMIN)[number];

export function podePromoverParaEstagiario(
  situacao: string,
  pessoa?: ReadCandidatoDto['pessoa'] | null,
): boolean {
  return situacao === 'convocada' && Boolean(pessoa?.id);
}

/** Correção administrativa de dados do candidato (ex.: e-mail): Administrador e Subdefensoria. */
export function podeCorrigirDadosCandidato(visao: number | string | null | undefined): boolean {
  const v = Number(visao);
  return v === NavigationView.Administrador || v === NavigationView.Subcorregedoria;
}

const RACAS_LABELS: Record<string, string> = {
  branca: 'Branca',
  preta: 'Preta',
  parda: 'Parda',
  amarela: 'Amarela',
  indigena: 'Indígena',
};

export function formatarSituacaoCandidato(situacao: string): string {
  return SITUACAO_LABELS[situacao] ?? situacao;
}

export function formatarGeneroCandidato(genero: string): string {
  return genero === 'masculino' ? 'Masculino' : genero === 'feminino' ? 'Feminino' : genero;
}

export function formatarRacaCandidato(raca: string): string {
  return RACAS_LABELS[raca] ?? raca;
}

/** Grupo da opção de vagas reservadas conforme a raça (alinha ao formulário de inscrição). */
export function rotuloGrupoVagasRaca(raca?: string | null): string | null {
  if (raca === 'indigena') return 'pessoas indígenas';
  if (raca === 'preta' || raca === 'parda') return 'pessoas negras';
  return null;
}

export function tituloOptaVagasReservadas(raca?: string | null): string {
  const grupo = rotuloGrupoVagasRaca(raca);
  return grupo ? `Opta vagas ${grupo}` : 'Opta vagas reservadas';
}

/** Pergunta completa (detalhes/formulário) conforme a raça. */
export function perguntaOptaVagasReservadas(raca?: string | null): string {
  if (raca === 'indigena') {
    return 'Opta por concorrer às vagas reservadas para pessoas indígenas?';
  }
  if (raca === 'preta' || raca === 'parda') {
    return 'Opta por concorrer às vagas reservadas para pessoas negras (pretos ou pardos)?';
  }
  return 'Opta por concorrer às vagas reservadas?';
}

/** Título da coluna na listagem: específico se todos os candidatos forem do mesmo grupo; genérico se mistos. */
export function tituloColunaOptaVagasReservadas(racas: Array<string | null | undefined>): string {
  const grupos = new Set(
    racas.map((raca) => rotuloGrupoVagasRaca(raca)).filter((grupo): grupo is string => Boolean(grupo)),
  );
  if (grupos.size === 1) {
    return `Opta vagas ${[...grupos][0]}`;
  }
  return 'Opta vagas reservadas';
}

export function formatarCpfExibicao(cpf: string | undefined): string {
  return cpf ? validaCPF(cpf, true) : '-';
}

export function formatarEstadoCivilExibicao(estadocivil: number | undefined | null): string {
  if (typeof estadocivil !== 'number')
    return '-';

  const found = EstadosCivisList.find((item) => item.value === estadocivil);
  return found?.name ?? '-';
}

export function formatarContatoCandidato(candidato: ReadCandidatoDto): string {
  const p = candidato.pessoa;
  if (p?.telefoneFormatado && p.telefoneFormatado.trim() !== '') {
    return p.telefoneFormatado;
  }

  if (p?.celular?.ddd && p.celular?.numero) {
    return `(${p.celular.ddd}) ${p.celular.numero}`;
  }

  if (p?.telefone?.ddd && p.telefone?.numero) {
    return `(${p.telefone.ddd}) ${p.telefone.numero}`;
  }

  return '-';
}

export function getEnderecoExibicao(candidato: ReadCandidatoDto, pessoa?: ReadPersonDto | null) {
  return candidato.pessoa?.endereco ?? pessoa?.endereco ?? null;
}

